const DEFAULT_MAX_SIDE = 2400;
const DEFAULT_JPEG_QUALITY = 0.88;

function jpegFilename(filename) {
    const name = filename || "image";
    const extensionStart = name.lastIndexOf(".");
    const basename = extensionStart > 0 ? name.slice(0, extensionStart) : name;
    return `${basename}.jpg`;
}

/**
 * Re-encode a browser image File as JPEG before upload.
 *
 * Images keep their original dimensions until their longest side exceeds
 * maxSide. Transparent pixels are flattened onto white so scans stay legible.
 * If the browser cannot decode or encode the file (for example, unsupported
 * HEIC), the original File is returned and the upload can continue normally.
 */
export async function compressImageFile(
    file,
    { maxSide = DEFAULT_MAX_SIDE, quality = DEFAULT_JPEG_QUALITY } = {},
) {
    // Resolve browser-only APIs at call time so importing this module in build
    // tools or Node-based tests remains safe.
    const runtime = typeof globalThis === "undefined" ? null : globalThis;
    const FileCtor = runtime?.File;
    const ImageCtor = runtime?.Image;
    const browserDocument = runtime?.document;
    const browserUrl = runtime?.URL;

    if (
        !FileCtor ||
        !(file instanceof FileCtor) ||
        !file.type?.toLowerCase().startsWith("image/") ||
        !ImageCtor ||
        !browserDocument?.createElement ||
        !browserUrl?.createObjectURL ||
        !browserUrl?.revokeObjectURL
    ) {
        return file;
    }

    let objectUrl;

    try {
        objectUrl = browserUrl.createObjectURL(file);
        const image = await new Promise((resolve, reject) => {
            const candidate = new ImageCtor();
            candidate.onload = () => resolve(candidate);
            candidate.onerror = () => reject(new Error("Image decode failed"));
            candidate.src = objectUrl;
        });

        const sourceWidth = image.naturalWidth || image.width;
        const sourceHeight = image.naturalHeight || image.height;
        if (!sourceWidth || !sourceHeight) return file;

        const safeMaxSide = Number.isFinite(maxSide) && maxSide > 0
            ? maxSide
            : DEFAULT_MAX_SIDE;
        const scale = Math.min(1, safeMaxSide / Math.max(sourceWidth, sourceHeight));
        const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
        const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

        const canvas = browserDocument.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const context = canvas.getContext("2d");
        if (!context || typeof canvas.toBlob !== "function") return file;

        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, targetWidth, targetHeight);
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = "high";
        context.drawImage(image, 0, 0, targetWidth, targetHeight);

        const safeQuality = Number.isFinite(quality)
            ? Math.min(1, Math.max(0, quality))
            : DEFAULT_JPEG_QUALITY;
        const blob = await new Promise((resolve) => {
            canvas.toBlob(resolve, "image/jpeg", safeQuality);
        });
        if (!blob) return file;

        return new FileCtor([blob], jpegFilename(file.name), {
            type: "image/jpeg",
            lastModified: file.lastModified,
        });
    } catch {
        return file;
    } finally {
        if (objectUrl) browserUrl.revokeObjectURL(objectUrl);
    }
}

